import React, { useState } from 'react';
import { 
  EditorHeader, 
  ErrorMessage, 
  StructureSidebar, 
  EmptyEditor, 
  AddSectionModal, 
  ConfirmationModal, 
  ActivityEditor,
  AiChatDialog
} from './components';
import { getPreviewRenderer } from '../../ai-agent/previewRenderers';

interface ModuleStructureEditorProps {
  isOpen: boolean;
  onClose: () => void;
  module: any;
  onSave?: (updatedModule: any) => Promise<void>;
}

const ModuleStructureEditor: React.FC<ModuleStructureEditorProps> = ({
  isOpen,
  onClose,
  module,
  onSave
}) => {
  const [moduleData, setModuleData] = useState<any>(module);
  const [showAddSectionModal, setShowAddSectionModal] = useState(false);
  const [showAIChatModal, setShowAIChatModal] = useState(false);
  const [inlineEditingSectionId, setInlineEditingSectionId] = useState<string | null>(null);
  const [showTypeSelector, setShowTypeSelector] = useState<string | null>(null);
  const [editingSectionNameId, setEditingSectionNameId] = useState<string | null>(null);
  const [editingActivityId, setEditingActivityId] = useState<string | null>(null);
  const [selectedActivityId, setSelectedActivityId] = useState<string | null>(null);
  const [newSectionName, setNewSectionName] = useState('');
  const [newSectionDeliveryMethod, setNewSectionDeliveryMethod] = useState('e-learning');
  const [newActivity, setNewActivity] = useState({
    title: '',
    type: 'lesson',
    description: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  
  // Confirmation modal states
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteItemType, setDeleteItemType] = useState<'section' | 'activity' | null>(null);
  const [deleteItemId, setDeleteItemId] = useState<string | null>(null);
  const [parentSectionId, setParentSectionId] = useState<string | null>(null);

  // Find the selected activity and its section
  const findSelectedActivity = () => {
    if (!selectedActivityId) return { activity: null, sectionId: null };
    
    for (const section of moduleData.sections || []) {
      for (const activity of section.activities || []) {
        if (activity.activity_id === selectedActivityId) {
          return { activity, sectionId: section.section_id };
        }
      }
    }
    return { activity: null, sectionId: null };
  };
  
  const { activity: selectedActivity, sectionId: selectedSectionId } = findSelectedActivity();

  const handleAddSection = () => {
    setShowAddSectionModal(true);
  };

  const handleShowAIAssistant = () => {
    setShowAIChatModal(true);
  };

  const handleShowTypeSelector = (sectionId: string) => {
    setShowTypeSelector(sectionId);
  };

  const handleSelectActivityType = (sectionId: string, type: string) => {
    setInlineEditingSectionId(sectionId);
    setShowTypeSelector(null);
    setNewActivity({
      title: '',
      type: type,
      description: ''
    });
  };

  const handleSaveModuleStructure = async (updatedModuleData: any) => {
    if (!onSave) return;
    
    try {
      setIsSaving(true);
      setSaveError(null);
      await onSave(updatedModuleData);
      // Don't close the editor after saving when adding a section or activity
    } catch (error) {
      console.error('Error saving module structure:', error);
      setSaveError(error instanceof Error ? error.message : 'Có lỗi xảy ra khi lưu cấu trúc module');
    } finally {
      setIsSaving(false);
    }
  };

  const handleGenerateAIContent = async (generatedContent: any) => {
    try {
      setIsGeneratingAI(true);
      setSaveError(null);
      
      // The generatedContent should contain the module structure
      if (!generatedContent.sections || generatedContent.sections.length === 0) {
        throw new Error('AI không tạo được nội dung. Vui lòng thử lại với yêu cầu khác.');
      }
      
      // Ensure each generated section and activity has a unique ID
      const processedSections = generatedContent.sections.map((section: any) => ({
        section_id: section.section_id || `section_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: section.name,
        delivery_method: section.delivery_method || 'e-learning',
        activities: (section.activities || []).map((activity: any) => ({
          activity_id: activity.activity_id || `activity_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          title: activity.title,
          type: activity.type || 'lesson',
          description: activity.description || ''
        }))
      }));
      
      // Create updated module data with the AI-generated sections
      const updatedModuleData = {
        ...moduleData,
        sections: [...(moduleData.sections || []), ...processedSections]
      };
      
      // Update local state
      setModuleData(updatedModuleData);
      
      // Close AI chat dialog
      setShowAIChatModal(false);
      
      // Auto-save the updated structure
      if (onSave) {
        await handleSaveModuleStructure(updatedModuleData);
      }
    } catch (error) {
      console.error('Error generating AI content:', error);
      setSaveError(error instanceof Error ? error.message : 'Có lỗi xảy ra khi tạo nội dung bằng AI');
    } finally {
      setIsGeneratingAI(false);
    }
  };

  const handleSaveSectionModal = async () => {
    if (!newSectionName.trim()) return;

    const newSection = {
      section_id: `section_${Date.now()}`,
      name: newSectionName,
      delivery_method: newSectionDeliveryMethod,
      activities: []
    };

    // Create updated module data with the new section
    const updatedModuleData = {
      ...moduleData,
      sections: [...(moduleData.sections || []), newSection]
    };

    // Update local state
    setModuleData(updatedModuleData);

    // Reset form and close modal
    setNewSectionName('');
    setNewSectionDeliveryMethod('e-learning');
    setShowAddSectionModal(false);

    // Auto-save the updated structure
    if (onSave) {
      await handleSaveModuleStructure(updatedModuleData);
    }
  };

  const handleSaveInlineActivity = async (sectionId: string) => {
    if (!newActivity.title.trim()) return;

    const newActivityItem = {
      activity_id: `activity_${Date.now()}`,
      title: newActivity.title,
      type: newActivity.type,
      description: newActivity.description
    };

    // Find the section and add the activity to it
    const updatedSections = moduleData.sections.map((section: any) => {
      if (section.section_id === sectionId) {
        return {
          ...section,
          activities: [...(section.activities || []), newActivityItem]
        };
      }
      return section;
    });

    // Create updated module data with the new activity
    const updatedModuleData = {
      ...moduleData,
      sections: updatedSections
    };

    // Update local state
    setModuleData(updatedModuleData);

    // Reset form and close inline editing
    setNewActivity({
      title: '',
      type: 'lesson',
      description: ''
    });
    setInlineEditingSectionId(null);

    // Auto-save the updated structure
    if (onSave) {
      await handleSaveModuleStructure(updatedModuleData);
    }
  };

  const handleCancelSectionModal = () => {
    setNewSectionName('');
    setNewSectionDeliveryMethod('e-learning');
    setShowAddSectionModal(false);
  };

  const handleCancelAIChatModal = () => {
    setShowAIChatModal(false);
  };

  const handleCancelInlineActivity = () => {
    setNewActivity({
      title: '',
      type: 'lesson',
      description: ''
    });
    setInlineEditingSectionId(null);
  };

  const handleCancelTypeSelector = () => {
    setShowTypeSelector(null);
  };

  const handleActivityChange = (activity: any) => {
    setNewActivity(activity);
  };

  // New handlers for inline editing
  const handleEditSectionName = (sectionId: string) => {
    // Cancel any other editing operations first
    setEditingActivityId(null);
    setInlineEditingSectionId(null);
    setShowTypeSelector(null);
    
    // Start editing section name
    setEditingSectionNameId(sectionId);
  };

  const handleSaveSectionName = async (sectionId: string, newName: string) => {
    if (!newName.trim()) return;

    // Update the section name in the module data
    const updatedSections = moduleData.sections.map((section: any) => {
      if (section.section_id === sectionId) {
        return {
          ...section,
          name: newName
        };
      }
      return section;
    });

    const updatedModuleData = {
      ...moduleData,
      sections: updatedSections
    };

    // Update local state
    setModuleData(updatedModuleData);
    setEditingSectionNameId(null);

    // Auto-save the updated structure
    if (onSave) {
      await handleSaveModuleStructure(updatedModuleData);
    }
  };

  const handleCancelEditSectionName = () => {
    setEditingSectionNameId(null);
  };

  const handleEditActivity = (activityId: string) => {
    // Cancel any other editing operations first
    setEditingSectionNameId(null);
    setInlineEditingSectionId(null);
    setShowTypeSelector(null);
    
    // Start editing activity
    setEditingActivityId(activityId);
  };

  const handleSaveActivityTitle = async (activityId: string, newTitle: string) => {
    if (!newTitle.trim()) return;

    // Find the activity and update its title
    let updatedSections = [...moduleData.sections];
    
    for (let i = 0; i < updatedSections.length; i++) {
      const section = updatedSections[i];
      const activityIndex = section.activities?.findIndex((activity: any) => activity.activity_id === activityId);
      
      if (activityIndex !== undefined && activityIndex >= 0) {
        // Found the activity, update its title
        updatedSections[i] = {
          ...section,
          activities: section.activities.map((activity: any, index: number) => {
            if (index === activityIndex) {
              return {
                ...activity,
                title: newTitle
              };
            }
            return activity;
          })
        };
        break;
      }
    }

    const updatedModuleData = {
      ...moduleData,
      sections: updatedSections
    };

    // Update local state
    setModuleData(updatedModuleData);
    setEditingActivityId(null);

    // Auto-save the updated structure
    if (onSave) {
      await handleSaveModuleStructure(updatedModuleData);
    }
  };

  const handleCancelEditActivity = () => {
    setEditingActivityId(null);
  };

  // Delete handlers
  const handleDeleteSection = (sectionId: string) => {
    // Show confirmation dialog
    setDeleteItemType('section');
    setDeleteItemId(sectionId);
    setShowDeleteConfirmation(true);
  };

  const handleDeleteActivity = (activityId: string, sectionId: string) => {
    // Show confirmation dialog
    setDeleteItemType('activity');
    setDeleteItemId(activityId);
    setParentSectionId(sectionId);
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteItemType || !deleteItemId) return;

    let updatedModuleData;

    if (deleteItemType === 'section') {
      // Remove the section from the module data
      updatedModuleData = {
        ...moduleData,
        sections: moduleData.sections.filter((section: any) => section.section_id !== deleteItemId)
      };
    } else if (deleteItemType === 'activity' && parentSectionId) {
      // Find the section and remove the activity from it
      const updatedSections = moduleData.sections.map((section: any) => {
        if (section.section_id === parentSectionId) {
          return {
            ...section,
            activities: section.activities.filter((activity: any) => activity.activity_id !== deleteItemId)
          };
        }
        return section;
      });

      updatedModuleData = {
        ...moduleData,
        sections: updatedSections
      };

      // If we're deleting the currently selected activity, clear the selection
      if (selectedActivityId === deleteItemId) {
        setSelectedActivityId(null);
      }
    }

    if (updatedModuleData) {
      // Update local state
      setModuleData(updatedModuleData);

      // Auto-save the updated structure
      if (onSave) {
        await handleSaveModuleStructure(updatedModuleData);
      }
    }

    // Reset delete state
    handleCancelDelete();
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setDeleteItemType(null);
    setDeleteItemId(null);
    setParentSectionId(null);
  };

  // New handler for selecting an activity
  const handleSelectActivity = (activityId: string) => {
    // Don't select if we're in edit mode
    if (editingActivityId || editingSectionNameId || inlineEditingSectionId || showTypeSelector) {
      return;
    }
    
    setSelectedActivityId(activityId);
  };

  // New handler for updating an activity's content
  const handleActivityUpdate = async (updatedActivity: any) => {
    if (!selectedActivity) return;
    
    // Find the section that contains this activity
    let updatedSections = [...moduleData.sections];
    let sectionFound = false;
    
    for (let i = 0; i < updatedSections.length; i++) {
      const section = updatedSections[i];
      const activityIndex = section.activities?.findIndex(
        (activity: any) => activity.activity_id === updatedActivity.activity_id
      );
      
      if (activityIndex !== undefined && activityIndex >= 0) {
        // Found the activity, update it
        updatedSections[i] = {
          ...section,
          activities: section.activities.map((activity: any, index: number) => {
            if (index === activityIndex) {
              return updatedActivity;
            }
            return activity;
          })
        };
        sectionFound = true;
        break;
      }
    }
    
    if (!sectionFound) {
      throw new Error('Could not find activity in module structure');
    }

    const updatedModuleData = {
      ...moduleData,
      sections: updatedSections
    };

    // Update local state
    setModuleData(updatedModuleData);

    // Save the updated module structure
    if (onSave) {
      await handleSaveModuleStructure(updatedModuleData);
    }
    
    return;
  };

  if (!isOpen || !module) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      
      {/* Dialog */}
      <div className="fixed inset-0 flex">
        <div className="relative w-full bg-white shadow-xl flex flex-col">
          {/* Header */}
          <EditorHeader 
            moduleName={moduleData.name}
            sectionCount={moduleData.sections?.length || 0}
            isSaving={isSaving}
            onClose={onClose}
          />

          {/* Error message */}
          <ErrorMessage message={saveError} />

          {/* Content */}
          <div className="flex-1 flex overflow-hidden">
            {/* Left Sidebar - Structure Tree */}
            <StructureSidebar 
              sections={moduleData.sections || []}
              showTypeSelector={showTypeSelector}
              inlineEditingSectionId={inlineEditingSectionId}
              editingSectionNameId={editingSectionNameId}
              editingActivityId={editingActivityId}
              selectedActivityId={selectedActivityId}
              newActivity={newActivity}
              isSaving={isSaving}
              onAddSection={handleAddSection}
              onShowAIAssistant={handleShowAIAssistant}
              onShowTypeSelector={handleShowTypeSelector}
              onSelectActivityType={handleSelectActivityType}
              onCancelTypeSelector={handleCancelTypeSelector}
              onCancelInlineActivity={handleCancelInlineActivity}
              onSaveInlineActivity={handleSaveInlineActivity}
              onActivityChange={handleActivityChange}
              onEditSectionName={handleEditSectionName}
              onSaveSectionName={handleSaveSectionName}
              onCancelEditSectionName={handleCancelEditSectionName}
              onEditActivity={handleEditActivity}
              onSaveActivityTitle={handleSaveActivityTitle}
              onCancelEditActivity={handleCancelEditActivity}
              onDeleteSection={handleDeleteSection}
              onDeleteActivity={handleDeleteActivity}
              onSelectActivity={handleSelectActivity}
            />

            {/* Main Editor Area */}
            <div className="flex-1 bg-white overflow-y-auto">
              {selectedActivity ? (
                <ActivityEditor 
                  activity={selectedActivity} 
                  onActivityUpdate={handleActivityUpdate}
                  moduleStructure={moduleData}
                  sectionId={selectedSectionId}
                />
              ) : (
                <div className="p-6">
                  <EmptyEditor />
                </div>
              )}
            </div>
          </div>

          {/* Add Section Modal */}
          <AddSectionModal 
            isOpen={showAddSectionModal}
            sectionName={newSectionName}
            deliveryMethod={newSectionDeliveryMethod}
            isSaving={isSaving}
            onSectionNameChange={setNewSectionName}
            onDeliveryMethodChange={setNewSectionDeliveryMethod}
            onSave={handleSaveSectionModal}
            onCancel={handleCancelSectionModal}
          />

          {/* AI Chat Dialog */}
          <AiChatDialog
            isOpen={showAIChatModal}
            onClose={handleCancelAIChatModal}
            title="Tạo cấu trúc module"
            welcomeMessage="Bắt đầu tạo cấu trúc module với trợ lý AI"
            welcomeSubMessage="Mô tả cấu trúc module bạn muốn tạo"
            inputPlaceholder="Mô tả cấu trúc module bạn muốn tạo..."
            onContentGenerated={handleGenerateAIContent}
            contextData={moduleData}
            chainName="module_structure_generator"
            renderPreview={(content) => {
              const ModuleStructurePreview = getPreviewRenderer('module_structure');
              return <ModuleStructurePreview content={content} />;
            }}
            quickPrompts={[
              { id: '1', text: 'Tạo module với 3 section', icon: '📚' },
              { id: '2', text: 'Thêm hoạt động tương tác', icon: '🎯' },
              { id: '3', text: 'Tạo bài kiểm tra', icon: '📝' },
              { id: '4', text: 'Thêm video bài giảng', icon: '🎥' }
            ]}
          />

          {/* Confirmation Modal for Delete */}
          <ConfirmationModal
            isOpen={showDeleteConfirmation}
            title={`Xóa ${deleteItemType === 'section' ? 'Section' : 'Hoạt động'}`}
            message={`Bạn có chắc chắn muốn xóa ${deleteItemType === 'section' ? 'section' : 'hoạt động'} này không? Hành động này không thể hoàn tác.`}
            confirmLabel="Xóa"
            cancelLabel="Hủy"
            isDestructive={true}
            onConfirm={handleConfirmDelete}
            onCancel={handleCancelDelete}
          />
        </div>
      </div>
    </div>
  );
};

export default ModuleStructureEditor; 